import { ActionButton } from '@if/mui'
import { CardActions, CardContent, CardHeader } from '@mui/material'
import { CardBorderTop } from '@src/components/common/Card'
import { useSplitApplication } from '@src/data/api/products-api/products-api'
import { ProductsWorksheet } from '@src/types/WorksheetSchema'
import { useTranslation } from 'react-i18next'

interface Props {
  financingTerm: number
  financedProductsWithTermShorterThanLoanTerm: { name: string; term: number }[]
  creditApplicationId: string
  unvalidatedWorksheet: ProductsWorksheet
  onNewApplicationCreated: (newAppId: string, updateWorksheetData: ProductsWorksheet) => void
}

const ProductsSplitApplicationPrompt = ({
  financingTerm,
  financedProductsWithTermShorterThanLoanTerm,
  creditApplicationId,
  unvalidatedWorksheet,
  onNewApplicationCreated,
}: Props) => {
  const { t } = useTranslation()

  const [splitApplication, isPending] = useSplitApplication()

  const handleSplitApplication = async () => {
    const response = await splitApplication({ creditApplicationId, unvalidatedWorksheet })
    onNewApplicationCreated(response.newCreditApplicationId, response.updatedWorksheet)
  }

  return (
    <CardBorderTop
      color="error"
      sx={{
        padding: 2,
        height: 'fit-content',
        maxWidth: { md: '25rem' },
        minWidth: '25rem',
      }}
    >
      <CardHeader title={t('productsWorksheet.splitApplicationPrompt.header')} />
      <CardContent>
        <p style={{ marginTop: 0 }}>
          {t('productsWorksheet.splitApplicationPrompt.financingTermMustBeLessThanOrEqual')}
        </p>
        <p>
          {t('productsWorksheet.splitApplicationPrompt.productsWithDurationLessThanSelectedTerm', { financingTerm })}
        </p>
        <ul>
          {financedProductsWithTermShorterThanLoanTerm.map((p) => (
            <li key={p.name}>
              {p.name} - {p.term} {t('productsWorksheet.splitApplicationPrompt.months')}
            </li>
          ))}
        </ul>
      </CardContent>
      <CardActions>
        <ActionButton
          fullWidth
          emphasis="principal"
          onClick={handleSplitApplication}
          loading={isPending}
          disabled={financedProductsWithTermShorterThanLoanTerm.length > 2}
        >
          {t('productsWorksheet.splitApplicationPrompt.moveProductsToNewApplication')}
        </ActionButton>
      </CardActions>
    </CardBorderTop>
  )
}

export default ProductsSplitApplicationPrompt
